import React from "react";
import { Drawer, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { ThreeDots } from "react-loader-spinner";
import { makeStyles } from "@material-ui/core/styles";
import theme from "../../theme";
import CommonButton from "../button";
import { ClaimStateChip } from "./StatusChip";
import { formatCurrency, formatDateTime, CLAIM_STATE_META } from "./utils";

const useStyles = makeStyles(() => ({
  paper: {
    width: "440px",
    maxWidth: "100vw",
    padding: "28px 32px 40px",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: "20px",
  },
  title: {
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: "22px",
  },
  row: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "14px 0",
    borderBottom: `1px solid ${theme.colors.Zumthor}`,
  },
  key: {
    color: theme.colors.DodgerBlue,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  value: {
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: "15px",
    wordBreak: "break-word",
  },
  mono: {
    fontFamily: "monospace",
    fontSize: "13px",
  },
  note: {
    backgroundColor: theme.colors.Zumthor,
    borderRadius: "8px",
    padding: "12px 14px",
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: "13px",
    margin: "20px 0",
  },
  loader: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "240px",
  },
}));

function Field({ label, children }) {
  const classes = useStyles();
  return (
    <div className={classes.row}>
      <span className={classes.key}>{label}</span>
      <span className={classes.value}>{children}</span>
    </div>
  );
}

function ClaimDetailDrawer({ open, claim, loading, onClose, onDelete }) {
  const classes = useStyles();
  const stateMeta = claim ? CLAIM_STATE_META[claim.state] : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.paper }}
    >
      <div className={classes.header}>
        <div className={classes.title}>Claim details</div>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </div>

      {loading && !claim ? (
        <div className={classes.loader}>
          <ThreeDots color={theme.colors.DodgerBlue} />
        </div>
      ) : claim ? (
        <>
          <Field label="Employee">{claim.employee_name || "—"}</Field>
          <Field label="Bonus">{claim.bonus_name || "—"}</Field>
          <Field label="Amount">{formatCurrency(claim.amount)}</Field>
          <Field label="State">
            <ClaimStateChip state={claim.state} />
          </Field>
          <Field label="Payment reference">
            {claim.payment ? (
              <span className={classes.mono}>{claim.payment}</span>
            ) : (
              "— (no payment linked yet)"
            )}
          </Field>
          <Field label="Redeemed at">{formatDateTime(claim.redeemed_at)}</Field>
          <Field label="Created at">{formatDateTime(claim.created_at)}</Field>

          {stateMeta?.description ? (
            <div className={classes.note}>{stateMeta.description}</div>
          ) : null}

          {/* Delete claim is intentionally hidden — claim deletion is risky for
              accounting reconciliation and is paused until a soft-delete /
              audit-trail flow is in place. Re-enable by uncommenting and
              ensuring the saga's confirmation path is wired.
          <CommonButton
            type="button"
            text="Delete claim"
            stylevariant="danger"
            fontSize="15px"
            onClick={() => onDelete && onDelete(claim)}
          />
          */}
        </>
      ) : (
        <div className={classes.value}>No claim selected.</div>
      )}
    </Drawer>
  );
}

export default ClaimDetailDrawer;
